import axios from 'axios';
import EventBus from 'eventing-bus';
import constants from '../../constants';
import { all, takeEvery, call, put, select } from 'redux-saga/effects';

// Create a new activity with the provided data
function* createActivity({ payload }) {
  yield put({ type: 'SET_ACTIVITY_LOADING', payload: true });

  const { data, successCallback } = payload;
  const attachments = data['attachments'] || [];
  const associations = data['associations'] || {};

  // Remove attachments from payload (uploaded separately)
  const activityData = { ...data };
  delete activityData['attachments'];

  const { error, response } = yield call(postCall, { path: '/activities/create', payload: activityData });
  if (error) {
    EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to create activity');
    yield put({ type: 'SET_ACTIVITY_LOADING', payload: false });
    return;
  }

  const createdActivity = response['data']['body'];

  // Upload attachments if any
  if (attachments.length > 0) {
    for (const file of attachments) {
      const formData = new FormData();
      formData.append('file', file['file'] || file);
      formData.append('objectType', 'activity');
      formData.append('objectId', createdActivity['_id']);
      formData.append('activityType', data['type'] || 'note');

      const { error: uploadError } = yield call(postCall, { path: '/attachments/upload', payload: formData });
      if (uploadError) EventBus.publish('error', 'Failed to upload attachment');
    }
    EventBus.publish('success', 'Activity created with attachments');
  } else EventBus.publish('success', response['data']['message']);

  // Refresh SinglePage data if on a single page view
  const singlePageState = yield select(state => state['SinglePage']);
  if (singlePageState?.['singlePageData']?.['_id']) {
    yield put({ type: 'GET_SINGLE_PAGE_DATA', payload: { objectType: singlePageState['singlePageData']['objectType'], id: singlePageState['singlePageData']['_id'] } });
  }

  // Refresh pipeline board if activity was created from pipeline view
  const activityState = yield select(state => state['Activities']);
  if (activityState['activitySource'] === 'pipeline') yield put({ type: 'GET_PIPELINE_BOARD' });

  // Cleanup
  if (successCallback) successCallback(createdActivity);
  yield put({ type: 'TOGGLE_ACTIVITY_MODAL', payload: { isOpen: false } });
  yield put({ type: 'CLEAR_PENDING_ATTACHMENTS' });
  yield put({ type: 'SET_ACTIVITY_LOADING', payload: false });
}

const sleep = (ms) => new Promise(resolve => setTimeout(resolve, ms));

// Update an existing activity
function* updateActivity({ payload }) {
  const attachments = payload['attachments'] || [];

  // Remove attachments from payload as they'll be uploaded separately
  const activityData = { ...payload };
  delete activityData['attachments'];

  const { error, response } = yield call(putCall, { path: `/activities/update/${payload['_id']}`, payload: activityData });
  if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to update activity');
  else if (response) {
    // Upload new attachments if any - use call to wait for each upload to complete
    let uploadedCount = 0;
    if (attachments.length > 0) {
      for (const file of attachments) {
        // Only upload files that are File objects (new uploads), skip existing attachments
        if (file['file'] instanceof File || file instanceof File) {
          const fileId = file['id'] || Date.now().toString();
          const actualFile = file['file'] || file;

          const formData = new FormData();
          formData.append('file', actualFile);
          formData.append('objectType', 'activity');
          formData.append('objectId', payload['_id']);
          formData.append('activityType', 'note');

          const { response: uploadResponse, error: uploadError } = yield call(postCall, { path: '/attachments/upload', payload: formData });

          if (uploadError) {
            yield put({ type: 'UPLOAD_ATTACHMENT_FAILURE', payload: { fileId, error: uploadError['message'] } });
            EventBus.publish('error', 'Failed to upload attachment');
          } else {
            yield put({ type: 'UPLOAD_ATTACHMENT_SUCCESS', payload: { fileId, attachment: uploadResponse['data']['body'] } });
            uploadedCount++;
          }
        }
      }
    }

    // Show success message
    if (uploadedCount > 0) {
      EventBus.publish('success', 'Activity and attachments updated successfully');
    } else {
      EventBus.publish('success', response['data']['message']);
    }

    // Refresh single page data which includes activities and attachments
    // This is done FIRST and ONLY to avoid race condition where old data overwrites new data
    const singlePageState = yield select(state => state['SinglePage']);
    if (singlePageState?.['singlePageData']?.['_id']) {
      yield put({
        type: 'GET_SINGLE_PAGE_DATA',
        payload: {
          objectType: singlePageState['singlePageData']['objectType'],
          id: singlePageState['singlePageData']['_id']
        }
      });
    } else {
      // If no single page context, refresh activities only
      if (payload['objectType'] && payload['objectId']) {
        yield put({ type: 'GET_OBJECT_ACTIVITIES', payload: { objectType: payload['objectType'], objectId: payload['objectId'] } });
      } else {
        // Fallback to checking Redux state associations
        const activityState = yield select(state => state['Activities']);
        const associations = activityState['associations'] || {};
        if (associations['deals']?.length > 0) {
          yield put({ type: 'GET_OBJECT_ACTIVITIES', payload: { objectType: 'deals', objectId: associations['deals'][0] } });
        } else if (associations['contacts']?.length > 0) {
          yield put({ type: 'GET_OBJECT_ACTIVITIES', payload: { objectType: 'contacts', objectId: associations['contacts'][0] } });
        } else if (associations['companies']?.length > 0) {
          yield put({ type: 'GET_OBJECT_ACTIVITIES', payload: { objectType: 'companies', objectId: associations['companies'][0] } });
        }
      }
    }

    yield put({ type: 'CLEAR_PENDING_ATTACHMENTS' });
  }
}

// Delete an activity by ID
function* deleteActivity({ payload }) {
  yield put({ type: 'SET_DELETE_ACTIVITY', payload });
  const { error, response } = yield call(deleteCall, `/activities/delete/${payload}`);
  if (error) return EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to delete activity');

  if (response) {
    EventBus.publish('success', 'Activity deleted successfully');

    // Refresh single page data to update attachments count in sidebar
    const singlePageState = yield select(state => state['SinglePage']);
    if (singlePageState?.['singlePageData']?.['_id']) {
      yield put({
        type: 'GET_SINGLE_PAGE_DATA',
        payload: {
          objectType: singlePageState['singlePageData']['objectType'],
          id: singlePageState['singlePageData']['_id']
        }
      });
    }
  }
}

// Complete an activity
function* completeActivity({ payload }) {
  const { id, outcome } = payload;
  const { error, response } = yield call(postCall, { path: `/activities/complete/${id}`, payload: { outcome } });
  if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to complete activity');
  else if (response) {
    // Refresh single page data to reflect the completed status
    const singlePageState = yield select(state => state['SinglePage']);
    if (singlePageState?.['singlePageData']?.['_id']) {
      yield put({
        type: 'GET_SINGLE_PAGE_DATA',
        payload: {
          objectType: singlePageState['singlePageData']['objectType'],
          id: singlePageState['singlePageData']['_id']
        }
      });
    }
  }
}

// Create a relation between objects
function* createRelation({ payload }) {
  const { error, response } = yield call(postCall, { path: `/relations/create`, payload });
  if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to create relation');
  // else if (response) {
  //   // Refresh the source object's data to show updated relations
  //   if (payload['sourceId'] && payload['relationType']) {
  //     // Extract object type from relationType (e.g., 'deal_contact' -> 'deal')
  //     const sourceObjectType = payload['relationType'].split('_')[1];
  //     console.log('********** sourceObjectType = ', sourceObjectType);
  //     const objectSchema = constants.getObjectSchema(sourceObjectType);
  //     console.log('********** objectSchema = ', objectSchema);
  //     yield put({ type: 'GET_SINGLE_PAGE_DATA', payload: { objectType: objectSchema?.plural?.toLowerCase(), id: payload['sourceId'] } });
  //   }
  // }
}

function* getObjectActivities({ payload }) {
  const { error, response } = yield call(getCall, `/activities/object/${payload['objectType']}/${payload['objectId']}`);
  if (error) {
    EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to get entity activities');
    return { success: false, error };
  }
  else if (response) {
    yield put({ type: 'SET_ACTIVITY', payload: response['data']['body'] });
    return { success: true, data: response['data']['body'] };
  }
}

function* addComment({ payload }) {
  const { payload: { activityId, commentId, content, objectType, objectId }, successCallback } = payload;
  const { error, response } = yield call(postCall, { path: `/activities/${activityId}/comment/${commentId}`, payload: { content } });
  if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to add comment');
  else if (response) {
    if (successCallback) successCallback(response['data']['body']);
    // EventBus.publish('success', response['data']['message']);
    // yield put({ type: 'GET_OBJECT_ACTIVITIES', payload: { objectType, objectId } });
  }
};

function* updateComment({ payload }) {
  const { payload: { activityId, commentId, content, objectType, objectId }, successCallback } = payload;
  const { error, response } = yield call(postCall, { path: `/activities/${activityId}/comment/${commentId}`, payload: { content } });
  if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to add comment');
  else if (response) {
    if (successCallback) successCallback();
    // EventBus.publish('success', response['data']['message']);
    // yield put({ type: 'GET_OBJECT_ACTIVITIES', payload: { objectType, objectId } });
  }
};

function* deleteComment({ payload }) {
  const { commentId, activityId, objectType, objectId } = payload;
  const { error, response } = yield call(deleteCall, `/activities/${activityId}/comment/${commentId}`);
  if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to add comment');
  // else if (response) {
  //   // EventBus.publish('success', response['data']['message']);
  //   yield put({ type: 'GET_OBJECT_ACTIVITIES', payload: { objectType, objectId } });
  // }
  yield put({ type: 'SET_DELETE_COMMENT', payload: { deleteCommentId: '', isDeleteComment: false } });
};

// Upload attachment
function* uploadAttachment({ payload }) {
  const { file, activityId, objectType, objectId } = payload;
  const fileId = file['id'] || Date.now().toString();
  const actualFile = file['file'] || file; // Handle both wrapped and unwrapped file objects

  const formData = new FormData();
  formData.append('file', actualFile);
  formData.append('objectType', objectType || 'activity');
  formData.append('objectId', objectId || activityId);
  if (activityId) formData.append('activityType', 'note'); // Can be dynamic based on activity type

  try {
    const { response, error } = yield call(postCall, { path: '/attachments/upload', payload: formData });

    if (error) {
      yield put({ type: 'UPLOAD_ATTACHMENT_FAILURE', payload: { fileId, error: error['message'] } });
      EventBus.publish('error', 'Failed to upload attachment');
    } else {
      yield put({ type: 'UPLOAD_ATTACHMENT_SUCCESS', payload: { fileId, attachment: response['data']['body'] } });
      EventBus.publish('success', 'Attachment uploaded successfully');

      // Refresh data based on objectType
      if (activityId && objectType === 'activity') {
        // Get current state to check if we need to refresh activities list
        const activityState = yield select(state => state['Activities']);

        // Refresh activities list if we have associations context
        const associations = activityState['associations'] || {};
        if (associations['deals']?.length > 0 || associations['contacts']?.length > 0 || associations['companies']?.length > 0) {
          // Determine the appropriate objectType and objectId for refresh
          let targetObjectType, targetObjectId;
          if (associations['deals']?.length > 0) {
            targetObjectType = 'deals';
            targetObjectId = associations['deals'][0];
          } else if (associations['contacts']?.length > 0) {
            targetObjectType = 'contacts';
            targetObjectId = associations['contacts'][0];
          } else if (associations['companies']?.length > 0) {
            targetObjectType = 'companies';
            targetObjectId = associations['companies'][0];
          }

          if (targetObjectId) {
            yield put({ type: 'GET_OBJECT_ACTIVITIES', payload: { objectType: targetObjectType, objectId: targetObjectId } });
          }
        }
      } else if (objectType && objectId && objectType !== 'activity') {
        const objectTypeMap = { 'company': 'companies', 'deal': 'deals', 'contact': 'contacts' };
        const pluralObjectType = objectTypeMap[objectType] || objectType;
        yield put({ type: 'GET_SINGLE_PAGE_DATA', payload: { objectType: pluralObjectType, id: objectId } });
      }
    }
  } catch (error) {
    yield put({ type: 'UPLOAD_ATTACHMENT_FAILURE', payload: { fileId, error: error['message'] } });
  }
}

// Get activity attachments
function* getActivityAttachments({ payload }) {
  const { error, response } = yield call(getCall, `/attachments/activity/${payload}`);
  if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to get attachments');
  else if (response) yield put({ type: 'SET_ACTIVITY_ATTACHMENTS', payload: { activityId: payload, attachments: response['data']['body'] } });
}

// Remove attachment
function* removeAttachmentSaga({ payload }) {
  const { error, response } = yield call(deleteCall, `/attachments/${payload}`);
  if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to remove attachment');
  else if (response) EventBus.publish('success', 'Attachment removed successfully');
}

// Get object attachments
function* getObjectAttachments({ payload }) {
  const { objectType, objectId } = payload;
  const { error, response } = yield call(getCall, `/attachments/object/${objectType}/${objectId}`);
  if (error) EventBus.publish('error', error['response']?.['data']?.['message'] || 'Failed to get attachments');
  else if (response) {
    // For now, we'll just return the attachments - in a real app, you'd dispatch an action to store them
    return response['data']['body'];
  }
}

// Generic Communication Sagas (SMS, Email, WhatsApp, Call)
// Note: Connection status checking moved to Integration store - use getIntegrationStatus(toolName)

function* getConversationHistory({ payload }) {
  const { identifier, channels = 'sms,whatsapp,email' } = payload;
  if (!identifier) return;

  yield put({ type: 'SET_CONVERSATION_LOADING', payload: true });
  try {
    const { error, response } = yield call(getCall, `/activities/conversation/${encodeURIComponent(identifier)}?channels=${channels}&limit=100`);
    if (error) {
      EventBus.publish('error', error?.['response']?.['data']?.['message'] || 'Failed to fetch conversation history');
      yield put({ type: 'SET_CONVERSATION_LOADING', payload: false });
    } else if (response) {
      const data = response['data']?.['body'] || {};
      yield put({ type: 'SET_CONVERSATION_HISTORY', payload: { grouped: data['grouped'] || {}, timeline: data['timeline'] || [], counts: data['counts'] || {} } });
    }
  } catch (err) {
    yield put({ type: 'SET_CONVERSATION_LOADING', payload: false });
  }
}

function* sendCommunication({ payload }) {
  const { channel, successCallback, ...requestPayload } = payload;

  yield put({ type: 'SET_CONVERSATION_SENDING', payload: true });
  try {
    const { error, response } = yield call(postCall, { path: `/activities/${channel}`, payload: requestPayload });

    if (error) {
      EventBus.publish('error', error?.['response']?.['data']?.['message'] || `Failed to send ${channel}`);
      yield put({ type: 'SET_CONVERSATION_SENDING', payload: false });
    } else if (response) {
      const successMessages = { sms: 'SMS sent successfully!', email: 'Email sent successfully!', whatsapp: 'WhatsApp sent successfully!', call: 'Call initiated successfully!' };
      EventBus.publish('success', successMessages[channel] || `${channel.toUpperCase()} sent successfully!`);

      // Add message to conversation - extract activity and format it
      const responseBody = response?.['data']?.['body'];
      if (responseBody && channel !== 'call') {
        const activity = responseBody['activity'] || responseBody;
        const formattedMessage = {
          _id: activity['_id'],
          channel: channel,
          message: activity['metadata']?.['body'] || activity['metadata']?.['message'] || requestPayload['message'] || activity['description'],
          direction: activity['direction'] || 'outbound',
          timestamp: activity['createdAt'] || new Date().toISOString(),
          from: activity['metadata']?.['from'] || activity['metadata']?.['fromNumber'],
          to: activity['metadata']?.['to'] || requestPayload['to']
        };
        yield put({ type: 'ADD_CONVERSATION_MESSAGE', payload: { channel, message: formattedMessage } });
      }

      yield put({ type: 'SET_CONVERSATION_SENDING', payload: false });

      if (successCallback) successCallback(responseBody);

      // Refresh single page data to show new activity
      const singlePageState = yield select(state => state['SinglePage']);
      if (singlePageState?.['singlePageData']?.['_id']) {
        yield call(sleep, 500);
        yield put({ type: 'GET_SINGLE_PAGE_DATA', payload: { objectType: singlePageState['singlePageData']['objectType'], id: singlePageState['singlePageData']['_id'] } });
      }
    }
  } catch (err) {
    EventBus.publish('error', `Failed to send ${channel}`);
    yield put({ type: 'SET_CONVERSATION_SENDING', payload: false });
  }
}

// Get conversation resources (agents, phones for calls)
function* getConversationResources({ payload }) {
  const { channel } = payload;

  if (channel === 'call') {
    yield put({ type: 'SET_CONVERSATION_LOADING', payload: true });
    try {
      // Fetch both agents and phones in parallel
      const [agentsResult, phonesResult] = yield all([
        call(getCall, '/agents'),
        call(getCall, '/phones')
      ]);

      const agents = agentsResult?.['response']?.['data']?.['body'] || [];
      const phones = phonesResult?.['response']?.['data']?.['body'] || [];

      yield put({ type: 'SET_CONVERSATION_RESOURCES', payload: { agents, phones } });
    } catch (err) {
      yield put({ type: 'SET_CONVERSATION_LOADING', payload: false });
    }
  }
}

// Fetch WhatsApp Templates
function* getWhatsAppTemplates() {
  const { error, response } = yield call(getCall, '/activities/whatsapp/templates');
  if (error) {
    yield put({ type: 'SET_WHATSAPP_TEMPLATES', payload: { templates: [], loading: false, error: error['response']?.['data']?.['message'] || 'Failed to fetch templates' } });
  } else {
    const templates = response?.['data']?.['body']?.['templates'] || [];
    yield put({ type: 'SET_WHATSAPP_TEMPLATES', payload: { templates, loading: false, error: null } });
  }
}

// Watcher saga for handling activity-related actions
function* actionWatcher() {
  yield takeEvery('CREATE_ACTIVITY', createActivity);
  yield takeEvery('UPDATE_ACTIVITY', updateActivity);
  yield takeEvery('CREATE_RELATION', createRelation);
  yield takeEvery('DELETE_ACTIVITY', deleteActivity);
  yield takeEvery('COMPLETE_ACTIVITY', completeActivity);
  yield takeEvery('GET_OBJECT_ACTIVITIES', getObjectActivities);
  yield takeEvery('ADD_COMMENT', addComment);
  yield takeEvery('UPDATE_COMMENT', updateComment);
  yield takeEvery('DELETE_COMMENT', deleteComment);
  yield takeEvery('UPLOAD_ATTACHMENT', uploadAttachment);
  yield takeEvery('GET_ACTIVITY_ATTACHMENTS', getActivityAttachments);
  yield takeEvery('REMOVE_ATTACHMENT', removeAttachmentSaga);
  yield takeEvery('GET_OBJECT_ATTACHMENTS', getObjectAttachments);
  // Conversation (SMS, WhatsApp, Email, Call)
  yield takeEvery('GET_CONVERSATION_HISTORY', getConversationHistory);
  yield takeEvery('SEND_COMMUNICATION', sendCommunication);
  yield takeEvery('GET_CONVERSATION_RESOURCES', getConversationResources);
  // WhatsApp Templates
  yield takeEvery('GET_WHATSAPP_TEMPLATES', getWhatsAppTemplates);
}

// Root saga for managing all activity-related actions
export default function* rootSaga() { yield all([actionWatcher()]) }

// Helper function to make POST requests to the server
function postCall({ path, payload, headers = {} }) {
  return axios
    .post(path, payload, {
      headers: {
        'Content-Type': payload instanceof FormData ? 'multipart/form-data' : 'application/json',
        ...headers,
      },
    })
    .then(response => ({ response }))
    .catch(error => {
      if (error.response?.status === 401) EventBus.publish('tokenExpired');
      return { error };
    });
}

// Helper function to make GET requests to the server
function getCall(path) {
  return axios
    .get(path)
    .then(response => ({ response }))
    .catch(error => {
      if (error.response?.status === 401) EventBus.publish('tokenExpired');
      return { error };
    });
}

// Helper function to make DELETE requests to the server
function deleteCall(path) {
  return axios
    .delete(path)
    .then(response => ({ response }))
    .catch(error => {
      if (error.response?.status === 401) EventBus.publish('tokenExpired');
      return { error };
    });
}

// Helper function to make PUT requests to the server
function putCall({ path, payload, headers = {} }) {
  return axios
    .put(path, payload, {
      headers: {
        'Content-Type': payload instanceof FormData ? 'multipart/form-data' : 'application/json',
        ...headers,
      },
    })
    .then(response => ({ response }))
    .catch(error => {
      if (error.response?.status === 401) EventBus.publish('tokenExpired');
      return { error };
    });
}